import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import PhygitalIcon from "@/components/PhygitalIcon";
import EarlyAccessModal from "@/components/EarlyAccessModal";
import SharedFooter from "@/components/SharedFooter";
import { Check, ArrowLeft, ArrowRight, Sparkles } from "lucide-react";
import { getCategoryPreset, CATEGORY_ORDER } from "../lib/categoryConfig";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const PricingPage = () => {
  const navigate = useNavigate();
  const [isAnnual, setIsAnnual] = useState(false);
  const [showEarlyAccessModal, setShowEarlyAccessModal] = useState(false);
  const [checkingOut, setCheckingOut] = useState(null);
  const [advocateDiscount, setAdvocateDiscount] = useState(null);

  useEffect(() => {
    document.title = "phygital - Pricing";
    window.scrollTo(0, 0);
    // Check for advocate referral code in URL
    const params = new URLSearchParams(window.location.search);
    const refCode = params.get("ref") || params.get("advocate");
    if (refCode) {
      fetch(`${API}/pricing/with-advocate?advocate_code=${refCode}`)
        .then(r => r.ok ? r.json() : null)
        .then(data => { if (data?.advocate_discount_applied) setAdvocateDiscount(data); })
        .catch(() => {});
    }
  }, []);

  const handleStartTrial = async () => {
    const token = localStorage.getItem("session_token");
    if (!token) { navigate("/login?redirect=/pricing"); return; }
    setCheckingOut("trial");
    try {
      const res = await fetch(`${API}/subscription/start-trial`, {
        method: "POST",
        headers: { "Authorization": `Bearer ${token}`, "Content-Type": "application/json" }
      });
      const data = await res.json();
      if (res.ok) { toast.success("Free trial started!"); navigate("/subscription"); }
      else { toast.error(data.detail || "Could not start trial"); }
    } catch { toast.error("Error starting trial"); }
    setCheckingOut(null);
  };

  const handleCheckout = async (plan) => {
    const token = localStorage.getItem("session_token");
    if (!token) { navigate("/login?redirect=/pricing"); return; }
    setCheckingOut(plan);
    try {
      const res = await fetch(`${API}/subscription/checkout`, {
        method: "POST",
        headers: { "Authorization": `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({
          plan: plan,
          billing_cycle: isAnnual ? "annual" : "monthly",
          origin_url: window.location.origin,
          include_hardware: false
        })
      });
      const data = await res.json();
      if (res.ok && data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        toast.error(data.detail || "Could not start checkout");
      }
    } catch { toast.error("Error creating checkout"); }
    setCheckingOut(null);
  };

  // Provider tiers - for service professionals
  const providerTiers = [
    {
      name: "Free",
      monthlyPrice: 0,
      annualPrice: 0,
      description: "Get started and explore",
      tagline: "Perfect for trying out the platform",
      features: [
        "Personal preference wallet",
        "Up to 5 client profiles",
        "Basic service menu",
        "Share preferences anywhere",
        "Community support"
      ],
      cta: "Get Started",
      highlight: false
    },
    {
      name: "Solo",
      monthlyPrice: 11,
      annualPrice: 110,
      description: "For independent professionals",
      tagline: "Everything you need to run your business",
      features: [
        "Unlimited client profiles",
        "Full service menu with upsells",
        "Custom intake flow (4 steps)",
        "Inspiration gallery + looks",
        "Client preference history",
        "Analytics dashboard",
        "Payment links (Venmo/CashApp/Zelle)",
        "Inner circle management",
        "Display settings control",
        "Priority support"
      ],
      cta: "Start Free Trial",
      highlight: true,
      popular: true
    }
    // Team plan ($49/mo) and Luxe plan hidden - admin can toggle visibility
  ];

  // Industry icons using Lucide - with demo links where available
  const DEMO_ROUTES = { hair: "/hair", nails: "/nail", makeup: "/mua", events: "/events", culinary: "/culinary", wellness: "/wellness", instructors: "/instructor", retail: "/retail", travel: "/travel", dispensary: "/dispensaries" };
  const industries = CATEGORY_ORDER.map(key => {
    const preset = getCategoryPreset(key);
    return { word: preset.name.toLowerCase(), icon: preset.icon, iconColor: preset.iconColor, bgColor: preset.bgColor, demo: DEMO_ROUTES[key] || null };
  });

  const formatPrice = (tier) => {
    if (tier.monthlyPrice === 0) return "$0";
    if (isAnnual) {
      return `$${tier.annualPrice}`;
    }
    return `$${tier.monthlyPrice}`;
  };

  const getPeriod = (tier) => {
    if (tier.monthlyPrice === 0) return "";
    return isAnnual ? "/year" : "/month";
  };

  const getSavings = (tier) => {
    if (tier.monthlyPrice === 0) return null;
    return tier.monthlyPrice * 2; // 2 months free
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Nav */}
      <nav className="border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <PhygitalIcon className="w-10 h-10" variant="light" />
              <span className="font-display text-2xl font-bold">phygital</span>
            </Link>
            <div className="flex items-center gap-4">
              <Link to="/login" className="text-gray-700 hover:text-gray-900 font-medium">
                Sign In
              </Link>
              <Button 
                onClick={() => setShowEarlyAccessModal(true)}
                className="rounded-full bg-black hover:bg-gray-800"
                data-testid="pricing-nav-early-access-btn"
              >
                Get Early Access
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Header */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-6xl mx-auto px-6 text-center">
          <Link to="/" className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-700 mb-6">
            <ArrowLeft className="w-4 h-4" />
            Back to home
          </Link>
          <h1 className="text-xl sm:text-3xl md:text-5xl font-display font-bold text-gray-900 mb-4">
            Simple, transparent pricing
          </h1>
          <p className="text-sm sm:text-lg text-gray-700 max-w-2xl mx-auto mb-8">
            Free for everyone to store preferences. Affordable plans for service providers.
          </p>
          
          {/* Annual/Monthly Toggle */}
          <div className="inline-flex items-center gap-3 bg-white rounded-full p-1 border border-gray-200">
            <button
              onClick={() => setIsAnnual(false)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                !isAnnual ? 'bg-black text-white' : 'text-gray-700 hover:text-gray-900'
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setIsAnnual(true)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors flex items-center gap-2 ${
                isAnnual ? 'bg-black text-white' : 'text-gray-700 hover:text-gray-900'
              }`}
            >
              Annual
              <span className={`text-xs px-2 py-0.5 rounded-full ${isAnnual ? 'bg-green-400 text-green-900' : 'bg-green-100 text-green-700'}`}>
                2 months free
              </span>
            </button>
          </div>
        </div>
      </section>

      {/* Free Forever Banner */}
      {advocateDiscount && (
        <section className="py-4 bg-gradient-to-r from-green-50 to-emerald-50 border-y border-green-200" data-testid="advocate-discount-banner">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <p className="text-green-800 font-semibold">
              Referred by {advocateDiscount.advocate_name} - You get 10% off!
            </p>
            <div className="flex justify-center gap-6 mt-2 text-sm">
              <div>
                <p className="text-gray-600">Solo Monthly</p>
                <p className="font-bold text-green-700">
                  <span className="line-through text-gray-400">${advocateDiscount.pricing?.solo?.monthly_base}</span>{" "}
                  ${advocateDiscount.pricing?.solo?.monthly_discounted}/mo
                </p>
              </div>
              <div>
                <p className="text-gray-600">Solo Yearly</p>
                <p className="font-bold text-green-700">
                  <span className="line-through text-gray-400">${advocateDiscount.pricing?.solo?.yearly_base}</span>{" "}
                  ${advocateDiscount.pricing?.solo?.yearly_discounted}/yr
                </p>
              </div>
              <div>
                <p className="text-gray-600">Team Monthly</p>
                <p className="font-bold text-green-700">
                  <span className="line-through text-gray-400">${advocateDiscount.pricing?.team?.monthly_base}</span>{" "}
                  ${advocateDiscount.pricing?.team?.monthly_discounted}/mo
                </p>
              </div>
              <div>
                <p className="text-gray-600">Team Yearly</p>
                <p className="font-bold text-green-700">
                  <span className="line-through text-gray-400">${advocateDiscount.pricing?.team?.yearly_base}</span>{" "}
                  ${advocateDiscount.pricing?.team?.yearly_discounted}/yr
                </p>
              </div>
            </div>
            <p className="text-xs text-green-600 mt-1">+ 2-month free trial on all plans</p>
          </div>
        </section>
      )}

      <section className="py-8 bg-gradient-to-r from-purple-50 to-pink-50 border-y border-purple-100">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="mb-3">
            <span className="text-purple-700 font-semibold text-base sm:text-lg">
              Your preference wallet is<br className="sm:hidden" /> free forever
            </span>
          </div>
          <p className="text-xs sm:text-base text-gray-700 mb-4">
            <span className="block sm:hidden">Store and share your preferences anywhere. No subscription needed.</span>
            <span className="hidden sm:block">Store and share your preferences for any service, any provider, any event.</span>
            <span className="hidden sm:block">No subscription needed. No hidden fees. Ever.</span>
          </p>
          <Button 
            onClick={() => navigate('/login?type=customer')}
            className="rounded-full bg-purple-600 hover:bg-purple-700 text-white"
          >
            Get Started for Free
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </section>

      {/* Provider Plans */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-6">
          <div className="flex items-center justify-center gap-3 mb-3">
            <Sparkles className="w-5 h-5 text-purple-600" />
            <h2 className="text-xl font-display font-bold text-gray-900">Provider Plans</h2>
          </div>
          <p className="text-center text-sm text-gray-600 mb-8">
            For service professionals: stylists, wellness providers, consultants, and more
          </p>
          
          <div className={`grid grid-cols-1 gap-6 max-w-2xl mx-auto ${providerTiers.length === 1 ? 'md:grid-cols-1 max-w-sm' : providerTiers.length === 2 ? 'md:grid-cols-2 max-w-2xl' : 'md:grid-cols-3 max-w-4xl'}`}>
            {providerTiers.map((tier, index) => (
              <div 
                key={index}
                className={`rounded-2xl p-6 relative ${
                  tier.highlight 
                    ? 'bg-white border-2 border-purple-400 shadow-lg' 
                    : 'bg-white border border-gray-200'
                }`}
              >
                {tier.popular && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-purple-600 text-white text-xs font-medium rounded-full">
                    Most Popular
                  </span>
                )}
                {tier.badge && (
                  <span className="inline-block px-2 py-1 bg-amber-400 text-black text-xs font-medium rounded mb-3">
                    {tier.badge}
                  </span>
                )}
                <h3 className="font-semibold text-lg text-gray-900">{tier.name}</h3>
                <div className="mt-2 mb-2">
                  <span className="text-3xl font-bold text-gray-900">{formatPrice(tier)}</span>
                  <span className="text-gray-600">{getPeriod(tier)}</span>
                </div>
                {isAnnual && tier.monthlyPrice > 0 && (
                  <p className="text-xs text-green-600 font-medium mb-2">
                    Save ${getSavings(tier)}/year
                  </p>
                )}
                <p className="text-sm text-gray-700 font-medium">{tier.description}</p>
                <p className="text-xs text-gray-500 mb-4">{tier.tagline}</p>
                <ul className="space-y-2 mb-6">
                  {tier.features.map((feature, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                      <Check className={`w-4 h-4 mt-0.5 flex-shrink-0 ${tier.highlight ? 'text-purple-500' : 'text-green-500'}`} />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
                <Button 
                  className={`w-full rounded-full ${tier.highlight ? 'bg-purple-600 hover:bg-purple-700' : 'bg-black hover:bg-gray-800'} text-white`}
                  onClick={() => {
                    if (tier.name === "Free") navigate("/get-started?plan=free");
                    else navigate("/get-started?plan=trial");
                  }}
                  disabled={checkingOut === tier.name.toLowerCase()}
                  data-testid={`pricing-provider-${tier.name.toLowerCase()}-btn`}
                >
                  {checkingOut === tier.name.toLowerCase() ? "Processing..." : tier.cta}
                </Button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases Section - Vector Icons with Demo Labels */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-2xl font-display font-bold text-center mb-8">
            Built for every service industry
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {industries.map((item, i) => {
              const IconComponent = item.icon;
              const content = (
                <div className="p-4 rounded-xl hover:shadow-md transition-all text-center relative" style={{ backgroundColor: item.bgColor }} >
                  <IconComponent className="w-6 h-6 mx-auto mb-2" style={{ color: item.iconColor }} />
                  <p className="text-sm font-semibold" style={{ color: item.iconColor }}>{item.word}</p>
                </div>
              );
              
              return item.demo ? (
                <Link key={i} to={item.demo}>
                  {content}
                </Link>
              ) : (
                <div key={i}>{content}</div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16">
        <div className="max-w-3xl mx-auto px-6">
          <h2 className="text-2xl font-display font-bold text-center mb-8">
            Frequently asked questions
          </h2>
          
          <div className="space-y-6">
            <div className="bg-gray-50 rounded-xl p-6">
              <h3 className="font-semibold mb-2">Is storing my preferences really free?</h3>
              <p className="text-gray-700 text-sm">Yes! Your personal preference wallet is completely free, forever. Store unlimited preferences and share them with any provider or event at no cost.</p>
            </div>
            <div className="bg-gray-50 rounded-xl p-6">
              <h3 className="font-semibold mb-2">Who needs a paid plan?</h3>
              <p className="text-gray-700 text-sm">Only service providers who want premium features like unlimited client profiles, team management, or advanced analytics. If you're a customer just storing preferences, it's 100% free.</p>
            </div>
            <div className="bg-gray-50 rounded-xl p-6">
              <h3 className="font-semibold mb-2">What industries does phygital support?</h3>
              <p className="text-gray-700 text-sm">We support any service-based business. Beauty, wellness, hospitality, retail, dispensaries, events, and more. If you serve customers with personalized preferences, phygital works for you.</p>
            </div>
            <div className="bg-gray-50 rounded-xl p-6">
              <h3 className="font-semibold mb-2">How do I upgrade later?</h3>
              <p className="text-gray-700 text-sm">You can upgrade from your Host Portal settings at any time. Your data and settings carry over seamlessly.</p>
            </div>
            <div className="bg-gray-50 rounded-xl p-6">
              <h3 className="font-semibold mb-2">Can I try before I subscribe?</h3>
              <p className="text-gray-700 text-sm">Absolutely. Start with the free tier (up to 5 clients) and upgrade when you're ready. Solo and Team plans also include a free trial.</p>
            </div>
            <div className="bg-gray-50 rounded-xl p-6">
              <h3 className="font-semibold mb-2">Do event hosts need to pay?</h3>
              <p className="text-gray-700 text-sm">Event hosting is free! You can create events, collect guest preferences, and manage RSVPs at no cost.</p>
            </div>
            <div className="bg-gray-50 rounded-xl p-6">
              <h3 className="font-semibold mb-2">What do I save with annual billing?</h3>
              <p className="text-gray-700 text-sm">Annual plans include 2 months free. That's $110/year on Solo (vs $132 monthly).</p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <SharedFooter />
      
      {/* Early Access Modal */}
      <EarlyAccessModal open={showEarlyAccessModal} onOpenChange={setShowEarlyAccessModal} />
    </div>
  );
};

export default PricingPage;
